import { makeAutoObservable, runInAction } from 'mobx'
import { each, find, isEmpty, keyBy, map } from '@dt/fe-utils/dist/lodash'
import feeClient from 'sdk/fee'
import { uuid } from '@dt/fe-utils'
import { getPlatform } from '@utils'
import { memberMobx, virtualOrderMobx } from '../mkt'
import { orderFactory as OrderFactory, storeMobx } from 'restaurant'
import payAPI from 'api/pay.api'
import MemberApi from 'api/member.api'
import appMobx from '../app/app.mobx'
import { bankCardMobx, payChannelMobx } from '@mobxStore'

export interface ICard {
  type: string;
  id: string;
  name?: string;
  number: string;
  balance?: number;
  isPayTip?: boolean;
  cardType?: TBankCartType;
}

export interface ITipOption {
  ratio?: number;
  amount?: number;
  noTip?: boolean;
  customTip?: boolean;
}

export type TPayScene = 'dining' | 'giftcard_buy' | 'coupons_buy'
export type TDiningOption = 'dineIn' | 'toGo' | 'delivery'
export type TClientType = 'mobile' | 'pos' | 'kiosk'

interface IOrder {
  scene: TPayScene;
  orderId: string;
  nono?: string;
  brandId: string | number;
  storeId?: string | number;
  diningOption?: TDiningOption;
  subtotal: number;
  serviceCharge: number;
  tax: number;
}

interface IInitOption {
  scene: TPayScene;
  orderId: string;
  pcid?: string;
  noOtherConfig?: boolean;
  noLock?: boolean;
}

interface IGetTipOptionRequest {
  brandId: string | number;
  storeId: string | number;
  diningOption: TDiningOption;
  clientType: TClientType;
  subtotal: number;
  tax: number;
}

async function getTipOptions (req: IGetTipOptionRequest): Promise<ITipOption[]> {
  const diningOptionMap = {
    'dinein': 'dineIn',
    'togo': 'toGo',
    'delivery': 'delivery',
  }

  const diningOption = diningOptionMap[req.diningOption]
  const params = {
    diningOption,
    clientType: 'Mobile',
    total: req.subtotal,
    tax: req.tax
  }

  console.debug('tip options params', params)
  // const result = fee.tip(params)
  const result = feeClient.tip(params)
  console.debug('tip options', result)

  const options: ITipOption[] = []
  each(result, x => {
    if (x.type === 'percent') {
      options.push({
        ratio: x.percent,
        amount: x.amount
      })
    } else if (x.type === 'amount') {
      options.push({ amount: x.amount })
    } else if (x.type === 'noTip') {
      options.push({ noTip: true, amount: 0 })
    } else if (x.type === 'custom') {
      options.push({ customTip: true })
    }
  })

  return options
}

const sceneUseTypeMap = {
  'dining': 'dc',
  'giftcard_buy': 'cz',
  'coupons_buy': 'goods',
}

const sceneOrderSourceMap = {
  'dining': 'dt',
  'giftcard_buy': 'dt',
  'coupons_buy': 'dt'
}

export interface IPaymentDisplay {
  name: string;
  cardNo?: string;
  amountPaid: number;
}

export interface ISurcharge {
  [key: string]: {
    id: string;
    name: string;
    paymentSurchargeConfigUuid: string;
    rate: number;
  };
}

class PayStore {
  constructor () {
    makeAutoObservable(this)
  }
  order: IOrder;
  prepayCreateId?: string;
  clientNumber = appMobx.deviceId;
  tipOptions: ITipOption[] = []
  tempGiftCards: ICard[] = []
  bankCards: ICard[] = []
  channelToMethods: Record<string, string> = {
    GiftCard: 'giftCard',
    MemberBalance: 'memberBalance',
    CardConnectBankCard: 'bankCardOnline',
    HeartLandBankCard: 'bankCardOnline',
    GlobalPaymentsBankCard: 'bankCardOnline',
    StripeApplePay: 'applePay',
    CitconAlipayWeb: 'alipay',
    CitconWechatPayWeb: 'wechatPay',
    CardConnectApplePay: 'applePay',
    GlobalPaymentsApplePay: 'applePay',
    WechatPayWeb: 'wechatPay',
    AlipayApp: 'alipay',
    PayPalWeb: 'paypal',
    ShengPayWechatWeb: 'wechatPay',
    ShengPayAlipayWeb: 'alipay',
    ShengPayWechatWebV2: 'wechatPay',
    ShengPayAlipayWebV2: 'alipay',
  }
  allChannelToMethodMap: Record<string, string> = {
    Cash: 'cash',
    BankCardPax: 'bankCard',
    MagtekFirstDataBankCard: 'bankCard',
    GiftCard: 'giftCard',
    MemberBalance: 'memberBalancePay',
    CardConnectBankCard: 'bankCardOnline',
    HeartLandBankCard: 'bankCardOnline',
    GlobalPaymentsBankCard: 'bankCardOnline',
    CitconAlipayBarcode: 'alipay',
    CitconAlipayF2f: 'alipay',
    CitconAlipayWeb: 'alipay',
    CitconWechatPayBarcode: 'wechatPay',
    CitconWechatPayNative: 'wechatPay',
    CitconWechatPayWeb: 'wechatPay',
    StripeApplePay: 'applePay',
    WechatPayBarcode: 'wechatPay',
    WechatPayNative: 'wechatPay',
    WechatPayWeb: 'wechatPay',
    AlipayBarcode: 'alipay',
    AlipayF2f: 'alipay',
    AlipayWeb: 'alipay',
    AlipayApp: 'alipay',
    ShengPayWechatBarcode: 'wechatPay',
    ShengPayWechatSmall: 'wechatPay',
    ShengPayWechatWeb: 'wechatPay',
    ShengPayAlipayBarcode: 'alipay',
    ShengPayAlipayWeb: 'alipay',
    ShengPayWechatBarcodeV2: 'wechatPay',
    ShengPayWechatSmallV2: 'wechatPay',
    ShengPayWechatWebV2: 'wechatPay',
    ShengPayAlipayBarcodeV2: 'alipay',
    ShengPayAlipayWebV2: 'alipay',
    PayPalWeb: 'paypal',
    DouyinCoupon: 'douyinCoupon',
    MeituanCoupon: 'meituanCoupon',
    KoubeiCoupon: 'KoubeiCoupon',
  }
  channelAccountMap: Record<string, any> = {}
  channels: any[] = []
  policy: string
  // 合规政策 null则表示未请求过
  compliancePolicy: string | null = null
  /** 附加费 */
  surchargeMap: ISurcharge = {}
  get firstAvaliableMethod () {
    if (!this.order) {
      return {}
    }
    const avaliable = ['bankCardOnline', 'giftCard', 'memberBalance', 'alipay', 'wechatPay', 'applePay'].filter(e => this.methodChannelMap[e])
    if (avaliable.length === 0) {
      return {}
    }
    let index = 0
    let method, card
    while (!method && index < avaliable.length) {
      method = avaliable[index]
      if (method === 'bankCardOnline') {
        card = this.bankCards.length > 0 ? this.bankCards[0] : undefined
        if (!card) {
          method = undefined
        }
      }
      if (method === 'giftCard') {
        card = this.giftCards.length > 0 ? this.giftCards[0] : undefined
        if (!card) {
          method = undefined
        }
      }
      index++
    }
    return { method, card }
  }
  get availableMethods () {
    if (!this.order) {
      return {}
    }
    const avaliable = ['bankCardOnline', 'giftCard', 'memberBalance', 'alipay', 'wechatPay', 'applePay'].filter(e => this.methodChannelMap[e])
    if (avaliable.length === 0) {
      return {}
    }
    const obj: any = {}
    each(avaliable, (item: any) => {
      const method = item
      const channel = this.methodChannelMap[item]
      let card
      if (method === 'bankCardOnline') {
        card = this.bankCards.length > 0 ? this.bankCards[0] : undefined
      }
      if (method === 'giftCard') {
        card = this.giftCards.length > 0 ? this.giftCards[0] : undefined
      }
      obj[method] = { method, card, channel }
    })
    return obj
  }
  get memberId () {
    const memberId = memberMobx.memberInfo.memberId
    return memberId && memberId !== 0 ? memberId : undefined
  }
  get methodChannelMap (): Record<string, string> {
    if (!this.order) {
      return {}
    }
    const result: Record<string, string> = {}
    each(this.channels, x => {
      const method = this.channelToMethods[x.channelType]
      if (method) {
        result[method] = x.channelType
      }
    })
    if (this.order.scene === 'giftcard_buy' || this.order.scene === 'coupons_buy') {
      delete result.giftCard
      delete result.memberBalance
    }
    const inWechat = Boolean(navigator.userAgent.match(/MicroMessenger/ig))
    const inAlipay = Boolean(navigator.userAgent.match(/AlipayClient/ig))
    if (!inWechat) {
      delete result.wechatPay
    }
    if (!inAlipay) {
      delete result.alipay
    }
    // if (inAlipay && result.alipay === 'AlipayWeb' && some(this.channels, x => x.channelType === 'AlipayApp')) {
    //   result.alipay = 'AlipayApp'
    // }
    return result
  }
  get memberGiftCards (): ICard[] {
    const cards = memberMobx.giftCardListForPay
    return cards.map((x: IMemberGiftCardInfo) => ({
      type: 'giftCard',
      id: String(x.memberCardId),
      number: x.cardNo,
      balance: x.balance,
      isPayTip: x.giftcard.isPayTip
    }))
  }
  get giftCards (): ICard[] {
    if (this.order.scene === 'giftcard_buy' || this.order.scene === 'coupons_buy') {
      return []
    }
    return [
      ...this.memberGiftCards,
      ...this.tempGiftCards
    ]
  }
  get channelSortMap () {
    const list = this.channels?.map((v: any, index: number) => ({
      channel: v.channelType,
      sort: index
    }))
    return keyBy(list, 'channel')
  }
  async initPayOrder (scene: TPayScene, orderId: string, data: any) {
    const { brandId, storeId, nono, eatType, serviceCharge, paidFee, tax } = data
    this.order = {
      scene,
      orderId,
      nono,
      brandId,
      storeId,
      diningOption: eatType,
      subtotal: paidFee,
      serviceCharge: serviceCharge,
      tax
    }
  }
  async init (options: IInitOption) {
    let { scene, orderId, pcid, noOtherConfig = false, noLock } = options
    if (scene === 'dining') {
      if (!pcid) {
        const { result: pc } = await payAPI.preByOrdersId(orderId) as any
        pcid = pc.prepayCreateId
      }
      if (!pcid) {
        throw new Error('Please start pay (call preCreate) first then go to pay.')
      }
      await payAPI.lockPreCreate(pcid, orderId, this.clientNumber, false)
    } else {
      if (!pcid) {
        const { result: pc } = await payAPI.preByOrdersId(orderId) as any
        if (pc && pc.prepayCreateId) pcid = pc.prepayCreateId
      }
    }
    if (scene === 'dining') {
      const { data } = await OrderFactory.create(orderId)
      console.log('dining ===>', data)
      if (!data) {
        alert('获取订单数据出错')
        return
      }
      const { brandId, storeId, nono, eatType, serviceCharge, paidFee, tax } = data as any
      runInAction(() => {
        this.prepayCreateId = pcid
        this.order = {
          scene,
          orderId,
          nono,
          brandId,
          storeId,
          diningOption: eatType,
          subtotal: paidFee,
          serviceCharge: serviceCharge,
          tax
        }
        if (!noOtherConfig) {
          this.loadPolicy()
          this.loadChannels()
          this.loadBankCards()
          this.loadTipOptions()
          this.loadSurcharges()
        }
      })
    } else if (scene === 'giftcard_buy' || scene === 'coupons_buy') {
      const data = await virtualOrderMobx.getVirtualOrder(orderId)
      if (!data) {
        alert('获取订单数据出错')
        return
      }
      const { brandId, storeId, amount } = data
      runInAction(() => {
        this.prepayCreateId = pcid
        this.order = {
          scene,
          orderId,
          brandId,
          storeId,
          subtotal: amount,
          serviceCharge: 0,
          tax: 0
        }
        if (!noOtherConfig) {
          this.loadPolicy()
          this.loadChannels()
          this.loadBankCards()
          this.loadSurcharges()
        }
      })
    }
  }

  async setOrder (data: IOrder) {
    runInAction(() => {
      this.order = { ...data }
      this.loadPolicy()
      this.loadChannels()
      this.loadBankCards()
      this.loadSurcharges()
    })
  }

  async loadPolicy () {
    const policy = await payAPI.getCompliancePolicy() as any
    runInAction(() => {
      this.policy = policy
    })
  }
  async loadChannels () {
    const useType = sceneUseTypeMap[this.order.scene]
    const { storeId } = this.order
    if (!payChannelMobx.allChannels[useType]) {
      await payChannelMobx.init(Number(storeId), true)
    }
    runInAction(() => {
      this.channels = useType === 'dc' ? payChannelMobx.channels : payChannelMobx.allChannels[useType]
    })
  }
  async loadSurcharges () {
    if (bo === 'cn') {
      return
    }
    const { brandId } = this.order
    const res = await payAPI.queryRechargeSurchargeConfigByBrandId(brandId ? Number(brandId) : null) as any
    runInAction(() => {
      if (res) {
        each(res, (value: any, key: string) => {
          const _value = value[0]
          this.surchargeMap[key] = {
            ..._value,
            id: _value.paymentSurchargeConfigUuid
          }
        })
      }
    })
  }
  async loadBankCards () {
    if (this.memberId) {
      const { brandId, storeId } = this.order
      const { result } = await payAPI.listCreditCard({
        brandId,
        storeId,
        memberId: this.memberId,
        useType: sceneUseTypeMap[this.order.scene],
      }) as any
      runInAction(() => {
        this.bankCards = ((result || []) as { creditCardNo: string; creditCardNumber: string; type: TBankCartType }[]).map(x => ({
          type: 'bankCardOnline',
          id: x.creditCardNo,
          number: x.creditCardNumber,
          cardType: x.type,
        }))
      })
    }
  }
  async loadTipOptions () {
    const { brandId, storeId, diningOption, subtotal, tax } = this.order
    const tipOptions = await getTipOptions({
      brandId,
      storeId,
      diningOption,
      clientType: 'mobile',
      subtotal,
      tax
    })
    runInAction(() => {
      this.tipOptions = tipOptions
    })
  }
  async loadAccount (channel: string) {
    const item = find(this.channels, x => x.channelType === channel)
    if (!item) return
    const accountId = item.accountId
    if (!accountId) return
    const { result, error } = await payAPI.listPaymenAccountExt({ accountId }) as any
    if (error) {
      throw error
    }
    runInAction(() => {
      this.channelAccountMap[channel] = result
    })
  }
  async authBankCard (channel: string, token: string, details?: any): Promise<ICard> {
    const { brandId, storeId } = this.order
    const { result, error } = await payAPI.authCreditCard({
      useType: sceneUseTypeMap[this.order.scene],
      brandId,
      storeId,
      authCode: token,
      channelType: channel,
      memberId: this.memberId,
      ...details
    }) as any

    if (error) {
      throw error
    }

    const card = this.bankCards.find(x => x.id === result.creditCardNo)

    if (!card) {
      const newCard = {
        type: 'bankCardOnline',
        id: result.creditCardNo,
        number: result.creditCardNumber,
        cardType: result.product,
      }
      runInAction(() => {
        this.bankCards.push(newCard)
      })
      const { storeId } = storeMobx.info
      const { memberId, type } = memberMobx.memberInfo
      bankCardMobx.init(storeId, memberId, type, true)
      return newCard
    } else {
      runInAction(() => {
        card.number = result.creditCardNumber
      })
      return card
    }
  }
  async deleteBankCard (card: ICard) {
    const index = this.bankCards.indexOf(card)

    if (index > -1) {
      await payAPI.deleteCreditCard({ creditCardNo: [card.id] })
      runInAction(() => {
        this.bankCards.splice(index, 1)
      })
      const { storeId } = storeMobx.info
      const { memberId, type } = memberMobx.memberInfo
      bankCardMobx.init(storeId, memberId, type, true)
    }
  }
  async pay ({
    ordersId,
    amount,
    tip,
    channel,
    channelExt,
    ext,
    surchargeInfo
  }: {
    ordersId: string;
    amount: number;
    tip: number;
    channel: string;
    channelExt?: any;
    ext?: any;
    surchargeInfo?: any;
  }): Promise<void> {
    const { brandId, storeId } = this.order
    const currency = channel === 'GlobalPaymentsBankCard' ? 'CAD' : 'USD'
    const { result, error } = await payAPI.pay({
      bo,
      prepayCreateId: this.prepayCreateId,
      clientNumber: this.clientNumber,
      orderSource: sceneOrderSourceMap[this.order.scene],
      useType: sceneUseTypeMap[this.order.scene],
      brandId,
      storeId,
      currency,
      opAppId: Config.appId || '',
      requestId: uuid(),
      ordersId,
      amount,
      channelType: {
        [channel]: {
          ext: channelExt
        }
      },
      tip,
      ext: {
        ...ext,
        ordersNo: this.order.nono,
        deviceType: 'mobile'
      },
      ...surchargeInfo
    }) as any
    if (error) {
      throw error
    } else {
      return result
    }
  }
  async batchPay (orderId: string, channels: any[], ext?: any): Promise<void> {
    const { brandId, storeId } = this.order
    const currency = channels?.some(c => c.channelType === 'GlobalPaymentsBankCard') ? 'CAD' : 'USD'
    const { result, error } = await payAPI.batchPay({
      bo,
      prepayCreateId: this.prepayCreateId,
      clientNumber: this.clientNumber,
      orderSource: sceneOrderSourceMap[this.order.scene],
      useType: sceneUseTypeMap[this.order.scene],
      brandId,
      storeId,
      currency,
      // 支付宝支付时，需要传入appId
      opAppId: Config.appId || '',
      requestId: uuid(),
      ordersId: orderId,
      channels,
      ext: {
        ...ext,
        ordersNo: this.order.nono,
        deviceType: 'mobile'
      }
    }) as any
    if (error) {
      throw new Error(error.message)
    } else {
      return result
    }
  }
  async addTempGiftCard (giftCard: IGiftCardInfo) {
    const card = {
      type: 'giftCard',
      id: giftCard.memberCardId,
      number: giftCard.cardNo,
      balance: giftCard.balance,
      isPayTip: giftCard.giftcard.isPayTip
    }
    runInAction(() => {
      this.tempGiftCards.push(card)
    })
    return card
  }
  async applePayMerchantValidate (params: { validationURL: string }) {
    const { brandId, storeId } = this.order
    const { result, error } = await payAPI.applePayMerchantValidate({
      orderSource: sceneOrderSourceMap[this.order.scene],
      useType: sceneUseTypeMap[this.order.scene],
      brandId,
      storeId,
      validationURL: params.validationURL,
    }) as any
    if (error) {
      throw new Error(error.message)
    } else {
      return result
    }
  }
  getPaymentListDisplay (payments: any[]): IPaymentDisplay[] {
    return map(payments, x => this.getPaymentDisplay(x))
  }
  getPaymentDisplay (payment: any): IPaymentDisplay {
    const { amountPaid, channelType, payTypeName } = payment
    const channel = Object.keys(channelType)[0]
    const channelExt = payment.channelType[channel].ext
    let name: string, cardNo: string | undefined
    if (channel.startsWith('CustomFlag_')) {
      name = payTypeName
    } else {
      const method = this.allChannelToMethodMap[channel]
      name = method ? i18n.t(`pay.${method}`) : i18n.t('pay.onlinePay')
      if (channel === 'GiftCard') {
        cardNo = channelExt.cardNumber
      } else if (channel === 'BankCardPax' || channel === 'MagtekFirstDataBankCard' || channel === 'CardConnectBankCard' || channel === 'HeartLandBankCard' || channel === 'GlobalPaymentsBankCard') {
        cardNo = channelExt.maskedPan
      }
    }
    return { name, cardNo, amountPaid }
  }

  async initCompliancePolicy () {
    if (this.compliancePolicy === null) {
      const compliancePolicy = await payAPI.getCompliancePolicy() as any
      runInAction(() => {
        this.compliancePolicy = compliancePolicy
      })
    }
  }
  async fetchGOpenIdAndSave () {
    const { isWeixinMiniProgram } = getPlatform()
    const memberData = await MemberApi.channelDetail({
      memberId: this.memberId
    })
    if (memberData) {
      if (isWeixinMiniProgram) {
        return { appId: memberData.appId, openId: memberData.openId }
      } else {
        memberMobx.setThirdAuthInfo({ gOpenId: memberData.gOpenId, openId: memberData.openId })
        return { openId: memberData.gOpenId }
      }
    }
  }
}

export default new PayStore()
